import type { FulfilmentIndexKey } from "../api";

export type ChannelKeyInput = string[] | string | null | undefined;
/** What a channel label needs from a keys row or a response row (place_climbers carry the same three fields). */
export type ChannelLabelRow = Pick<FulfilmentIndexKey, "channel" | "place_id" | "place_label">;

const parts = (key: string) => key.split(":");
const groupName = (group: string) => group === "amazon" ? "Amazon" : group === "shopify" ? "Shopify" : group;
/** The selectors an input holds: a URL value may join several with commas. */
export const tokens = (input: ChannelKeyInput) => {
  const values = Array.isArray(input) ? input : input ? [input] : [];
  return values.flatMap((value) => value.split(",")).map((value) => value.trim()).filter(Boolean);
};
const leafList = (leaves: readonly string[]) => [...new Set(leaves)].sort((a, b) => a.localeCompare(b));
const matches = (leaf: string, selector: string) => selector === "all" || leaf === selector || leaf.startsWith(`${selector}:`);

/** Expand picker selectors to the exact leaf set sent to the engine (BR-008). */
export function canonicalize(input: ChannelKeyInput, leaves: readonly string[]): string[] {
  const universe = leafList(leaves);
  const selected = tokens(input);
  if (selected.length === 0 || selected.includes("all")) return universe;
  const output = new Set<string>();
  for (const selector of selected) {
    const found = universe.filter((leaf) => matches(leaf, selector));
    if (found.length === 0) throw new Error("unknown_place");
    found.forEach((leaf) => output.add(leaf));
  }
  return [...output].sort((a, b) => a.localeCompare(b));
}

/**
 * Render-safe canonicalize (bug 3fd0402f-8183): a selector the universe does not hold is dropped instead of thrown, so an
 * empty universe (the keys route has not answered: pending, not unknown) resolves to no leaves. Never throws.
 */
export function resolveForRender(input: ChannelKeyInput, leaves: readonly string[]): string[] {
  const universe = leafList(leaves);
  const selected = tokens(input);
  const known = selected.filter((selector) => universe.some((leaf) => matches(leaf, selector)));
  return selected.length > 0 && known.length === 0 ? [] : canonicalize(known, universe);
}

function compact(resolved: readonly string[], leaves: readonly string[]): string[] {
  const universe = leafList(leaves);
  const chosen = new Set(resolved);
  if (chosen.size === universe.length) return ["all"];
  const output: string[] = [];
  const groups = [...new Set(universe.map((leaf) => parts(leaf)[0]))].sort();
  for (const group of groups) {
    const groupLeaves = universe.filter((leaf) => parts(leaf)[0] === group);
    if (groupLeaves.every((leaf) => chosen.has(leaf))) { output.push(group); continue; }
    const regions = [...new Set(groupLeaves.map((leaf) => parts(leaf)[1]))].sort();
    for (const region of regions) {
      const regionLeaves = groupLeaves.filter((leaf) => parts(leaf)[1] === region);
      if (regionLeaves.every((leaf) => chosen.has(leaf))) output.push(`${group}:${region}`);
      else output.push(...regionLeaves.filter((leaf) => chosen.has(leaf)));
    }
  }
  return output;
}

/** Toggle a branch as a set of leaves, then collapse complete branches back to concise URL keys. */
export function toggle(input: ChannelKeyInput, key: string, leaves: readonly string[]): string[] {
  const universe = leafList(leaves);
  const current = new Set(canonicalize(input, universe));
  const branch = key === "all" ? universe : universe.filter((leaf) => matches(leaf, key));
  if (branch.length === 0) throw new Error("unknown_place");
  const remove = branch.every((leaf) => current.has(leaf));
  branch.forEach((leaf) => remove ? current.delete(leaf) : current.add(leaf));
  if (current.size === 0) return compact(branch, universe);
  return compact([...current], universe);
}

export function label(key: string, rows: readonly ChannelLabelRow[] = []): string {
  if (key === "all") return "All channels";
  const [group, region, place] = parts(key);
  const groupLabel = groupName(group);
  if (!region) return groupLabel;
  if (!place) return `${groupLabel} · ${region.toUpperCase()}`;
  return rows.find((row) => row.place_id === place && row.channel.toLowerCase() === group)?.place_label ?? place;
}

export function resolvedRegion(input: ChannelKeyInput, leaves: readonly string[]): string | null {
  const regions = new Set(canonicalize(input, leaves).map((leaf) => parts(leaf)[1]?.toUpperCase()).filter(Boolean));
  return regions.size === 1 ? [...regions][0] : null;
}

export const channelLeaf = (row: Pick<FulfilmentIndexKey, "channel" | "region" | "place_id">) => `${row.channel.toLowerCase()}:${row.region.toLowerCase()}:${row.place_id}`;

/** The scope line's channel piece, as the approved prototype's channelLabel(): All channels, a group, "Amazon EU", a place; more than two picks read "N channels". */
export function channelScopeLabel(input: ChannelKeyInput, rows: readonly ChannelLabelRow[] = []): string {
  const selected = tokens(input);
  if (selected.length === 0 || selected.includes("all")) return "All channels";
  const labels = selected.map((key) => {
    const [group, region, place] = parts(key);
    return region && !place ? `${groupName(group)} ${region.toUpperCase()}` : label(key, rows);
  });
  return labels.length > 2 ? `${labels.length} channels` : labels.join(" + ");
}
